"use client";

import {
  PickersDay,
  type PickersDayProps,
} from "@mui/x-date-pickers/PickersDay";
import type { DateTime } from "luxon";

export interface SuggestedPickersDayProps extends PickersDayProps {
  /**
   * Days to mark as "suggested" (e.g. recommended appointment slots). Compared
   * day-of-year against `props.day`. Matched days get `data-suggested="true"`
   * so the MuiPickersDay theme override paints the suggested-state ring.
   */
  suggestedDays?: DateTime[];
}

const isSameDay = (a: DateTime, b: DateTime) => a.hasSame(b, "day");

/**
 * Wrapper around `PickersDay` that adds support for a "Suggested" state.
 * Wire it via `slots.day` on the DatePicker and pass the array via
 * `slotProps.day.suggestedDays`.
 */
export const SuggestedPickersDay = ({
  suggestedDays,
  day,
  ...rest
}: SuggestedPickersDayProps) => {
  // `day` is typed as a union of every installed picker adapter's date type
  // (e.g. Luxon DateTime | Dayjs) when more than one adapter is in scope.
  // We pin to Luxon since the surrounding LocalizationProvider uses AdapterLuxon.
  const luxonDay = day as DateTime;
  const isSuggested =
    suggestedDays?.some((d) => isSameDay(d, luxonDay)) ?? false;
  return (
    <PickersDay
      {...rest}
      day={day}
      data-suggested={isSuggested ? "true" : undefined}
    />
  );
};
